import {
  Timestamp,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  FirestoreProductUploadDocument,
  ProductUploadParseResult,
  ProductUploadRecord,
  ProductUploadSource,
  ProductUploadStatus,
} from "@dashboard/types/product-upload";
import { readProductUploadRecord } from "@dashboard/utils/product-upload";

export type CreateProductUploadInput = {
  id: string;
  restaurantUuid: string;
  productName: string;
  assignedProductName?: string | null;
  photoUploaderUserId: string;
  status: ProductUploadStatus;
  source?: ProductUploadSource;
  assignedToEmail?: string | null;
  assignedToName?: string | null;
  imageUrls?: string[];
  productId?: string | null;
  parseResult?: ProductUploadParseResult | null;
  processedAtMillis?: number | null;
  errorMessage?: string | null;
};

export type UpdateProductUploadInput = Partial<{
  productId: string | null;
  productName: string | null;
  status: ProductUploadStatus;
  imageUrls: string[];
  parseResult: ProductUploadParseResult | null;
  reviewedIngredientIds: string[] | null;
  reviewedMayContainAllergenIds: string[] | null;
  processedAtMillis: number | null;
  errorMessage: string | null;
}>;

export interface ProductUploadApi {
  fetchProductUploads(restaurantUuid: string): Promise<ProductUploadRecord[]>;
  fetchProductUploadsForUploader(
    userId: string
  ): Promise<ProductUploadRecord[]>;
  createProductUpload(
    input: CreateProductUploadInput
  ): Promise<ProductUploadRecord>;
  updateProductUpload(
    restaurantUuid: string,
    productUploadId: string,
    updates: UpdateProductUploadInput
  ): Promise<void>;
  subscribeToProductUploads(
    restaurantUuid: string,
    onData: (records: ProductUploadRecord[]) => void,
    onError: (error: Error) => void
  ): () => void;
}

export class FirestoreBackedProductUploadApi implements ProductUploadApi {
  private firestoreRefs: FirestoreRefs;

  constructor(params: { firestoreRefs: FirestoreRefs }) {
    this.firestoreRefs = params.firestoreRefs;
  }

  public async fetchProductUploads(
    restaurantUuid: string
  ): Promise<ProductUploadRecord[]> {
    const snapshot = await getDocs(
      query(
        this.firestoreRefs.productUploads(restaurantUuid),
        orderBy("createdAt", "desc")
      )
    );

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as FirestoreProductUploadDocument;
        return readProductUploadRecord({ ...data, id: data.id ?? doc.id });
      })
      .filter((record): record is ProductUploadRecord => record !== null);
  }

  public async fetchProductUploadsForUploader(
    userId: string
  ): Promise<ProductUploadRecord[]> {
    const snapshot = await getDocs(
      query(
        this.firestoreRefs.productUploadsCollectionGroup(),
        where("photoUploaderUserId", "==", userId)
      )
    );

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as FirestoreProductUploadDocument;
        return readProductUploadRecord({ ...data, id: data.id ?? doc.id });
      })
      .filter((record): record is ProductUploadRecord => record !== null)
      .sort((a, b) => b.uploadedAtMillis - a.uploadedAtMillis);
  }

  public async createProductUpload(
    input: CreateProductUploadInput
  ): Promise<ProductUploadRecord> {
    const now = Timestamp.now();
    const processedAt =
      input.processedAtMillis === undefined || input.processedAtMillis === null
        ? null
        : Timestamp.fromMillis(input.processedAtMillis);
    const doc: FirestoreProductUploadDocument = {
      id: input.id,
      restaurantUuid: input.restaurantUuid,
      productId: input.productId ?? null,
      productName: input.productName,
      assignedProductName: input.assignedProductName ?? null,
      status: input.status,
      photoUploaderUserId: input.photoUploaderUserId,
      assignedToEmail: input.assignedToEmail ?? null,
      assignedToName: input.assignedToName ?? null,
      source: input.source ?? "dashboard_direct",
      imageUrls: input.imageUrls ?? [],
      parseResult: input.parseResult ?? null,
      errorMessage: input.errorMessage ?? null,
      createdAt: now,
      updatedAt: now,
      processedAt,
    };

    await setDoc(
      this.firestoreRefs.productUpload(input.restaurantUuid, input.id),
      doc
    );

    const record = readProductUploadRecord(doc);
    if (!record) {
      throw new Error("Created invalid product upload record");
    }

    return record;
  }

  public async updateProductUpload(
    restaurantUuid: string,
    productUploadId: string,
    updates: UpdateProductUploadInput
  ): Promise<void> {
    const { processedAtMillis, ...rest } = updates;
    const firestoreUpdates: Record<string, unknown> = {
      ...rest,
      updatedAt: Timestamp.now(),
    };

    if (processedAtMillis !== undefined) {
      firestoreUpdates.processedAt =
        processedAtMillis === null
          ? null
          : Timestamp.fromMillis(processedAtMillis);
    }

    await setDoc(
      this.firestoreRefs.productUpload(restaurantUuid, productUploadId),
      firestoreUpdates,
      { merge: true }
    );
  }

  public subscribeToProductUploads(
    restaurantUuid: string,
    onData: (records: ProductUploadRecord[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return onSnapshot(
      query(
        this.firestoreRefs.productUploads(restaurantUuid),
        orderBy("createdAt", "desc")
      ),
      (snapshot) => {
        const records = snapshot.docs
          .map((doc) => {
            const data = doc.data() as FirestoreProductUploadDocument;
            return readProductUploadRecord({
              ...data,
              id: data.id ?? doc.id,
            });
          })
          .filter((record): record is ProductUploadRecord => record !== null);
        onData(records);
      },
      onError
    );
  }
}
